// frontend/src/pages/StaticDocumentsPage.tsx - File-based document repository
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PageLayout } from '@/components/layout/PageLayout';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { cn, formatDate, formatFileSize } from '@/utils';
import { useAuthStore } from '@/store/auth';
import { useAppStore } from '@/store/app';
import { useConfirmation } from '@/components/ui/ConfirmationDialog';
import api from '@/api';

import {
  useStaticDocuments,
  useCreateStaticDocument,
  useUpdateStaticDocument,
  useDeleteStaticDocument
} from '@/hooks/useStaticDocuments';

import { useProjects } from '@/hooks/useProjects';
import { useGroups } from '@/hooks/useGroups';

import type {
  StaticDocument,
  Project,
  Group,
  StaticDocumentQueryParams,
  StaticDocumentUpdateRequest
} from '@/api/types';

// Debounced value hook
function useDebounced<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
}

// File type icon mapping
const FILE_TYPE_ICONS: Record<string, { icon: string; color: string; label: string }> = {
  'application/pdf': { icon: 'PDF', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300', label: 'PDF' },
  'application/msword': { icon: 'DOC', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300', label: 'Word' },
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': { icon: 'DOCX', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300', label: 'Word' },
  'application/vnd.ms-excel': { icon: 'XLS', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300', label: 'Excel' },
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': { icon: 'XLSX', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300', label: 'Excel' },
  'application/vnd.ms-powerpoint': { icon: 'PPT', color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300', label: 'PowerPoint' },
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': { icon: 'PPTX', color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300', label: 'PowerPoint' },
  'text/plain': { icon: 'TXT', color: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300', label: 'Text' },
  'text/csv': { icon: 'CSV', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300', label: 'CSV' },
  'image/jpeg': { icon: 'JPG', color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300', label: 'Image' },
  'image/png': { icon: 'PNG', color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300', label: 'Image' },
};

function getFileTypeInfo(contentType: string) {
  return FILE_TYPE_ICONS[contentType] || {
    icon: contentType.split('/')[1]?.toUpperCase().slice(0, 4) || 'FILE',
    color: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
    label: contentType.split('/')[1] || 'File'
  };
}

// ===== UPLOAD MODAL =====

const UploadModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onUpload: (files: File[], metadata: { description?: string; tags?: string[]; isPublic?: boolean; category?: string; projectId?: string }) => Promise<void>;
  projects: Project[];
  groups: Group[];
  isSubmitting?: boolean;
}> = ({ isOpen, onClose, onUpload, projects, groups, isSubmitting = false }) => {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [tagInput, setTagInput] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [isPublic, setIsPublic] = useState(false);
  const [projectId, setProjectId] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (isOpen) {
      setSelectedFiles([]);
      setDescription('');
      setCategory('');
      setTagInput('');
      setTags([]);
      setIsPublic(false);
      setProjectId('');
      setErrors({});
    }
  }, [isOpen]);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedFiles.length === 0) {
      setErrors({ files: 'Please select at least one file' });
      return;
    }
    try {
      await onUpload(selectedFiles, {
        description: description.trim(),
        tags: tags.filter(t => t.trim()),
        isPublic,
        category: category.trim(),
        projectId: projectId || undefined,
      });
      onClose();
    } catch (error: any) {
      setErrors({ general: error.message || 'Upload failed' });
    }
  }, [selectedFiles, description, tags, isPublic, category, projectId, onUpload, onClose]);

  const handleAddTag = useCallback(() => {
    if (tagInput.trim() && !tags.includes(tagInput.trim())) {
      setTags(prev => [...prev, tagInput.trim()]);
      setTagInput('');
    }
  }, [tagInput, tags]);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setSelectedFiles(Array.from(e.target.files));
      setErrors({});
    }
  }, []);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose} />

        <div className="inline-block w-full max-w-2xl p-6 my-8 text-left align-middle transition-all transform bg-white dark:bg-gray-800 shadow-xl rounded-lg">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Upload Documents</h3>
            <button onClick={onClose} disabled={isSubmitting} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {errors.general && (
              <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                <p className="text-sm text-red-600 dark:text-red-400">{errors.general}</p>
              </div>
            )}

            {/* File Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Files *</label>
              <div className={cn(
                "border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:border-blue-400 transition-colors",
                errors.files ? "border-red-300 dark:border-red-600" : "border-gray-300 dark:border-gray-600"
              )}>
                <input
                  type="file"
                  multiple
                  onChange={handleFileChange}
                  disabled={isSubmitting}
                  className="hidden"
                  id="file-upload"
                />
                <label htmlFor="file-upload" className="cursor-pointer">
                  <svg className="mx-auto h-10 w-10 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                    <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                    Click to select files or drag and drop
                  </p>
                  <p className="mt-1 text-xs text-gray-500">PDF, Word, Excel, PowerPoint, images, text (max 100MB each)</p>
                </label>
              </div>
              {selectedFiles.length > 0 && (
                <div className="mt-3 space-y-1">
                  {selectedFiles.map((file, i) => (
                    <div key={i} className="flex items-center justify-between text-sm bg-gray-50 dark:bg-gray-700 px-3 py-1.5 rounded">
                      <span className="text-gray-700 dark:text-gray-300 truncate">{file.name}</span>
                      <span className="text-gray-500 dark:text-gray-400 ml-2 flex-shrink-0">{formatFileSize(file.size)}</span>
                    </div>
                  ))}
                </div>
              )}
              {errors.files && <p className="text-red-600 dark:text-red-400 text-xs mt-1">{errors.files}</p>}
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                disabled={isSubmitting}
                maxLength={1000}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                placeholder="Brief description of the document(s)"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Category</label>
                <Input
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  placeholder="e.g. Policy, Template, SOP"
                  disabled={isSubmitting}
                />
              </div>

              {/* Project */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Project</label>
                <select
                  value={projectId}
                  onChange={(e) => setProjectId(e.target.value)}
                  disabled={isSubmitting}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                >
                  <option value="">No project</option>
                  {projects.map(p => (
                    <option key={p._id || p.id} value={p._id || p.id}>{p.projectName || p.name}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Tags */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Tags</label>
              <div className="flex space-x-2">
                <Input
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  placeholder="Add tag"
                  disabled={isSubmitting}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddTag(); } }}
                />
                <Button type="button" onClick={handleAddTag} disabled={isSubmitting || !tagInput.trim()} size="sm">Add</Button>
              </div>
              {tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {tags.map(tag => (
                    <span key={tag} className="inline-flex items-center px-2 py-0.5 text-xs bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300 rounded">
                      {tag}
                      <button type="button" onClick={() => setTags(prev => prev.filter(t => t !== tag))} className="ml-1 hover:text-blue-600">x</button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Public toggle */}
            <div className="flex items-center">
              <input
                type="checkbox"
                id="isPublic"
                checked={isPublic}
                onChange={(e) => setIsPublic(e.target.checked)}
                disabled={isSubmitting}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label htmlFor="isPublic" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                Visible to all organization members
              </label>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
              <Button type="button" variant="secondary" onClick={onClose} disabled={isSubmitting}>Cancel</Button>
              <Button type="submit" disabled={isSubmitting || selectedFiles.length === 0}>
                {isSubmitting ? (
                  <><LoadingSpinner size="sm" /><span className="ml-2">Uploading...</span></>
                ) : (
                  `Upload ${selectedFiles.length > 0 ? `(${selectedFiles.length} file${selectedFiles.length > 1 ? 's' : ''})` : ''}`
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// ===== EDIT MODAL =====

const EditModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: StaticDocumentUpdateRequest) => Promise<void>;
  document?: StaticDocument;
  isSubmitting?: boolean;
}> = ({ isOpen, onClose, onSave, document, isSubmitting = false }) => {
  const [filename, setFilename] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [tagInput, setTagInput] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [isPublic, setIsPublic] = useState(false);

  useEffect(() => {
    if (document && isOpen) {
      setFilename(document.filename || '');
      setDescription(document.description || '');
      setCategory(document.category || '');
      setTags(document.tags || []);
      setIsPublic(document.isPublic || false);
      setTagInput('');
    }
  }, [document, isOpen]);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await onSave({
        filename: filename.trim() || undefined,
        description: description.trim(),
        category: category.trim(),
        tags: tags.filter(t => t.trim()),
        isPublic,
      });
      onClose();
    } catch {}
  }, [filename, description, category, tags, isPublic, onSave, onClose]);

  const handleAddTag = useCallback(() => {
    if (tagInput.trim() && !tags.includes(tagInput.trim())) {
      setTags(prev => [...prev, tagInput.trim()]);
      setTagInput('');
    }
  }, [tagInput, tags]);

  if (!isOpen || !document) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose} />

        <div className="inline-block w-full max-w-lg p-6 my-8 text-left align-middle transition-all transform bg-white dark:bg-gray-800 shadow-xl rounded-lg">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-5">Edit Document</h3>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Filename</label>
              <Input value={filename} onChange={(e) => setFilename(e.target.value)} disabled={isSubmitting} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                disabled={isSubmitting}
                maxLength={1000}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Category</label>
              <Input value={category} onChange={(e) => setCategory(e.target.value)} disabled={isSubmitting} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Tags</label>
              <div className="flex space-x-2">
                <Input value={tagInput} onChange={(e) => setTagInput(e.target.value)} placeholder="Add tag" disabled={isSubmitting}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddTag(); } }} />
                <Button type="button" onClick={handleAddTag} disabled={isSubmitting || !tagInput.trim()} size="sm">Add</Button>
              </div>
              {tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {tags.map(tag => (
                    <span key={tag} className="inline-flex items-center px-2 py-0.5 text-xs bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300 rounded">
                      {tag}
                      <button type="button" onClick={() => setTags(prev => prev.filter(t => t !== tag))} className="ml-1">x</button>
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div className="flex items-center">
              <input type="checkbox" id="editIsPublic" checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)} disabled={isSubmitting}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
              <label htmlFor="editIsPublic" className="ml-2 text-sm text-gray-700 dark:text-gray-300">Visible to all organization members</label>
            </div>
            <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
              <Button type="button" variant="secondary" onClick={onClose} disabled={isSubmitting}>Cancel</Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? <><LoadingSpinner size="sm" /><span className="ml-2">Saving...</span></> : 'Save Changes'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// ===== DOCUMENT ROW =====

const DocumentRow: React.FC<{
  document: StaticDocument;
  onEdit: (doc: StaticDocument) => void;
  onDelete: (id: string) => void;
  onDownload: (id: string) => void;
  canEdit: boolean;
  canDelete: boolean;
}> = ({ document, onEdit, onDelete, onDownload, canEdit, canDelete }) => {
  const docId = document._id || document.id || '';
  const { confirm } = useConfirmation();
  const fileInfo = getFileTypeInfo(document.contentType);

  const handleDelete = useCallback(async () => {
    const confirmed = await confirm({
      title: 'Delete Document',
      message: `Are you sure you want to delete "${document.filename}"? This action cannot be undone.`,
      confirmText: 'Delete',
      variant: 'danger',
    });
    if (confirmed) onDelete(docId);
  }, [docId, document.filename, onDelete, confirm]);

  return (
    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
      {/* File type badge + name */}
      <td className="px-4 py-3">
        <div className="flex items-center space-x-3">
          <span className={cn('px-2 py-1 text-xs font-bold rounded', fileInfo.color)}>
            {fileInfo.icon}
          </span>
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-900 dark:text-white truncate max-w-xs" title={document.filename}>
              {document.filename}
            </p>
            {document.description && (
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-xs" title={document.description}>
                {document.description}
              </p>
            )}
          </div>
        </div>
      </td>

      {/* Category */}
      <td className="px-4 py-3">
        {document.category ? (
          <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400 rounded">
            {document.category}
          </span>
        ) : (
          <span className="text-xs text-gray-400">-</span>
        )}
      </td>

      {/* Tags */}
      <td className="px-4 py-3">
        {document.tags && document.tags.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {document.tags.slice(0, 2).map(tag => (
              <span key={tag} className="px-1.5 py-0.5 text-xs bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300 rounded">
                {tag}
              </span>
            ))}
            {document.tags.length > 2 && (
              <span className="px-1.5 py-0.5 text-xs text-gray-500">+{document.tags.length - 2}</span>
            )}
          </div>
        ) : (
          <span className="text-xs text-gray-400">-</span>
        )}
      </td>

      {/* Size */}
      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
        {formatFileSize(document.size)}
      </td>

      {/* Visibility */}
      <td className="px-4 py-3">
        <span className={cn(
          'px-2 py-0.5 text-xs font-medium rounded-full',
          document.isPublic
            ? 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-300'
            : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
        )}>
          {document.isPublic ? 'Public' : 'Private'}
        </span>
      </td>

      {/* Date */}
      <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400">
        {formatDate(document.createdAt)}
      </td>

      {/* Downloads */}
      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 text-center">
        {document.downloadCount || 0}
      </td>

      {/* Actions */}
      <td className="px-4 py-3">
        <div className="flex items-center space-x-1">
          <button
            onClick={() => onDownload(docId)}
            className="p-1.5 text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20"
            title="Download"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </button>
          {canEdit && (
            <button
              onClick={() => onEdit(document)}
              className="p-1.5 text-gray-500 hover:text-yellow-600 dark:text-gray-400 dark:hover:text-yellow-400 rounded hover:bg-yellow-50 dark:hover:bg-yellow-900/20"
              title="Edit"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </button>
          )}
          {canDelete && (
            <button
              onClick={handleDelete}
              className="p-1.5 text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400 rounded hover:bg-red-50 dark:hover:bg-red-900/20"
              title="Delete"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          )}
        </div>
      </td>
    </tr>
  );
};

// ===== MAIN PAGE =====

export const StaticDocumentsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const { globalError, setGlobalError, clearGlobalError } = useAppStore();
  const { user: currentUser } = useAuthStore();

  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [editingDocument, setEditingDocument] = useState<StaticDocument | undefined>();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const debouncedSearchTerm = useDebounced(searchTerm, 500);

  const queryParams = useMemo((): StaticDocumentQueryParams => ({
    page: 1,
    limit: 50,
    search: debouncedSearchTerm || undefined,
    category: categoryFilter !== 'all' ? categoryFilter : undefined,
    sortBy: 'createdAt',
    sortOrder: 'desc'
  }), [debouncedSearchTerm, categoryFilter]);

  const {
    data: documentsData,
    loading: documentsLoading,
    error: documentsError,
    refetch: refetchDocuments
  } = useStaticDocuments(queryParams);

  const { data: projectsData } = useProjects();

  const { data: groupsData } = useGroups(
    (currentUser?.organization as any)?._id || (currentUser?.organization as any)?.id || currentUser?.organization || undefined
  );

  const createDocumentMutation = useCreateStaticDocument({
    onSuccess: () => {
      clearGlobalError();
      refetchDocuments();
      setIsUploadModalOpen(false);
    },
    onError: (error: any) => {
      setGlobalError(error.message || 'Failed to upload document');
    }
  });

  const updateDocumentMutation = useUpdateStaticDocument({
    onSuccess: () => {
      clearGlobalError();
      refetchDocuments();
      setIsEditModalOpen(false);
      setEditingDocument(undefined);
    },
    onError: (error: any) => {
      setGlobalError(error.message || 'Failed to update document');
    }
  });

  const deleteDocumentMutation = useDeleteStaticDocument({
    onSuccess: () => {
      clearGlobalError();
      refetchDocuments();
    },
    onError: (error: any) => {
      setGlobalError(error.message || 'Failed to delete document');
    }
  });

  const documents = documentsData?.data || [];
  const projects = projectsData?.data || [];
  const groups = groupsData?.data || [];

  const canCreate = currentUser?.role === 'admin' || currentUser?.role === 'contributor' || currentUser?.role === 'implementor';
  const canEdit = currentUser?.role === 'admin' || currentUser?.role === 'contributor' || currentUser?.role === 'implementor';
  const canDelete = currentUser?.role === 'admin' || currentUser?.role === 'implementor';

  // Error handling
  useEffect(() => {
    if (documentsError) setGlobalError(documentsError);
  }, [documentsError, setGlobalError]);

  // Get unique categories
  const categories = useMemo(() => {
    const cats = new Set<string>();
    documents.forEach(doc => { if (doc.category) cats.add(doc.category); });
    return Array.from(cats).sort();
  }, [documents]);

  // Stats
  const stats = useMemo(() => {
    const totalSize = documents.reduce((sum, d) => sum + (d.size || 0), 0);
    const publicCount = documents.filter(d => d.isPublic).length;
    return { totalSize, publicCount };
  }, [documents]);

  const handleUpload = useCallback(async (files: File[], metadata: any) => {
    await createDocumentMutation.mutate({ files, metadata });
  }, [createDocumentMutation]);

  const handleEdit = useCallback((doc: StaticDocument) => {
    setEditingDocument(doc);
    setIsEditModalOpen(true);
  }, []);

  const handleSaveEdit = useCallback(async (data: StaticDocumentUpdateRequest) => {
    if (!editingDocument) return;
    await updateDocumentMutation.mutate({
      id: editingDocument._id || editingDocument.id || '',
      data
    });
  }, [editingDocument, updateDocumentMutation]);

  const handleDelete = useCallback(async (id: string) => {
    await deleteDocumentMutation.mutate(id);
  }, [deleteDocumentMutation]);

  const handleDownload = useCallback(async (id: string) => {
    setDownloadingId(id);
    try {
      const response = await api.staticDocuments.download(id);
      const data = (response as any)?.data || response;
      const url = data?.downloadUrl || data?.presignedUrl;
      if (url) {
        window.open(url, '_blank');
      } else {
        setGlobalError('Download URL not available');
      }
    } catch (err: any) {
      setGlobalError(err.message || 'Download failed');
    } finally {
      setDownloadingId(null);
    }
  }, [setGlobalError]);

  const actions = useMemo(() => (
    <div className="flex items-center space-x-3">
      <Input
        placeholder="Search documents..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        className="w-56"
      />
      <select
        value={categoryFilter}
        onChange={(e) => setCategoryFilter(e.target.value)}
        className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
      >
        <option value="all">All Categories</option>
        {categories.map(cat => (
          <option key={cat} value={cat}>{cat}</option>
        ))}
      </select>
      <Button onClick={() => refetchDocuments()} variant="secondary" disabled={documentsLoading}>
        {documentsLoading ? <LoadingSpinner size="sm" /> : 'Refresh'}
      </Button>
      {canCreate && (
        <Button onClick={() => setIsUploadModalOpen(true)} disabled={documentsLoading}>
          Upload Document
        </Button>
      )}
    </div>
  ), [searchTerm, categoryFilter, categories, documentsLoading, canCreate, refetchDocuments]);

  return (
    <PageLayout
      title="Documents"
      subtitle={`Central document repository \u00b7 ${documents.length} documents \u00b7 ${formatFileSize(stats.totalSize)} total`}
      actions={actions}
      loading={documentsLoading && documents.length === 0}
    >
      {globalError && (
        <div className="mb-4">
          <ErrorAlert message={globalError} onClose={clearGlobalError} />
        </div>
      )}

      {documentsLoading && documents.length === 0 ? (
        <div className="flex items-center justify-center h-96">
          <LoadingSpinner size="lg" />
        </div>
      ) : documents.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-12 text-center">
          <svg className="mx-auto h-16 w-16 text-gray-300 dark:text-gray-600 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No documents yet</h3>
          <p className="text-gray-500 dark:text-gray-400 mb-6 max-w-md mx-auto">
            Upload templates, policies, SOPs, and reference materials to create your organization's central document repository.
          </p>
          {canCreate && (
            <Button onClick={() => setIsUploadModalOpen(true)}>Upload Your First Document</Button>
          )}
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Document</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Category</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Tags</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Size</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Visibility</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Uploaded</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Downloads</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {documents.map(doc => {
                  const docId = doc._id || doc.id || '';
                  if (!docId) return null;
                  return (
                    <DocumentRow
                      key={docId}
                      document={doc}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                      onDownload={handleDownload}
                      canEdit={canEdit}
                      canDelete={canDelete}
                    />
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Upload Modal */}
      <UploadModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        onUpload={handleUpload}
        projects={projects}
        groups={groups}
        isSubmitting={createDocumentMutation.loading}
      />

      {/* Edit Modal */}
      <EditModal
        isOpen={isEditModalOpen}
        onClose={() => { setIsEditModalOpen(false); setEditingDocument(undefined); }}
        onSave={handleSaveEdit}
        document={editingDocument}
        isSubmitting={updateDocumentMutation.loading}
      />
    </PageLayout>
  );
};
